import { Avatar, Pagination, Table, Tooltip } from "flowbite-react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import {
  ALL_COLUMN_KEYS,
  COLUMN_TOOLTIPS,
  FORECAST_FILTER_KEYS,
  FORECAST_FILTER_SPECIFIC_COLUMNS,
  LEGACY_FILTER_KEYS,
} from "./ForecastPage.utils";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useForecastContext } from "../../contexts/ForecastContext";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";
import { Link } from "react-router-dom";
import {
  TooltipContent,
  ScrollableTableWrapper,
  PaginationInfo,
} from "../../components";

const getTotalRowForRenewalForecast = (
  opportunities,
  totalRowTitle,
  grandTotals,
) => {
  // For renewal forecast, use the grand totals from the API (already formatted)
  return {
    oppType: totalRowTitle,
    accounts: grandTotals?.accounts || "—",
    contracts: grandTotals?.contracts || 0,
    deltaMrr: grandTotals?.deltaMrr || "€0",
    deltaArr: grandTotals?.deltaArr || "€0",
  };
};

const getTotalRowForDetailedView = (opportunities, totalRowTitle, totals) => {
  return {
    contractName: "",
    accountName: totalRowTitle,
    csmName: "",
    oppType: "",
    oppName: "",
    stage: "",
    closeDate: "",
    deltaMrr: totals?.deltaMrr || "€0",
    deltaArr: totals?.deltaArr || "€0",
    pctChange: "",
    logoLost: "",
  };
};

const ITEMS_PER_PAGE = 25;

export const ForecastPageTableTemplate = ({
  title,
  totalRowTitle,
  opportunities = [],
  selectedFilter,
  loading = false,
  totals = null,
  grandTotals = null,
  titleTooltip = {},
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when opportunities change
  useEffect(() => {
    setCurrentPage(1);
  }, [opportunities]);

  useEmptyStateTracking("forecast_table", {
    isEmpty: opportunities.length === 0,
    isLoading: loading,
    properties: { filter: selectedFilter ?? null },
  });

  // Calculate pagination
  const totalPages = Math.ceil(opportunities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedOpportunities = opportunities.slice(startIndex, endIndex);

  // Map new filter keys to legacy column configurations
  const getColumnConfigKey = (filter) => {
    switch (filter) {
      case LEGACY_FILTER_KEYS.RENEWAL_FORECAST:
        return FORECAST_FILTER_KEYS.REVENUE_FORECAST; // Maps to per-type view
      case LEGACY_FILTER_KEYS.PIPELINE_EVOLUTION:
        return FORECAST_FILTER_KEYS.OPEN_OPPORTUNITIES; // Maps to per-account open view
      case LEGACY_FILTER_KEYS.RENEWED_ACCOUNTS:
        return FORECAST_FILTER_KEYS.CLOSED_OPPORTUNITIES; // Maps to per-account closed view
      default:
        return FORECAST_FILTER_KEYS.REVENUE_FORECAST;
    }
  };

  const columnConfigKey = getColumnConfigKey(selectedFilter);
  const columnKeys = FORECAST_FILTER_SPECIFIC_COLUMNS[columnConfigKey] || [];
  const isRenewalForecast =
    selectedFilter === LEGACY_FILTER_KEYS.RENEWAL_FORECAST;

  if (loading) {
    return (
      <div className="mt-1 flex w-full flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
        <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
          <div className="text-2xl font-bold leading-7 text-gray-800">
            {title}
          </div>
          <HiOutlineInformationCircle className="mt-1 opacity-50" />
        </div>
        <div className="flex h-64 w-full items-center justify-center">
          <div className="animate-pulse text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  const totalRow = isRenewalForecast
    ? getTotalRowForRenewalForecast(
        opportunities,
        totalRowTitle,
        grandTotals || {},
      )
    : getTotalRowForDetailedView(opportunities, totalRowTitle, totals || {});

  return (
    <div className="mt-1 flex w-full min-w-0 flex-1 flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
      <div className="mb-2 flex items-center justify-start gap-2 px-6 pt-5">
        <div className="text-2xl font-bold leading-7 text-gray-800">
          {title}
        </div>
        <Tooltip
          content={
            <TooltipContent
              title={titleTooltip.title}
              size="lg"
              text={titleTooltip.text}
            />
          }
          // eslint-disable-next-line react/style-prop-object
          style="light"
        >
          <HiOutlineInformationCircle className="mt-1 opacity-50" />
        </Tooltip>
      </div>

      {opportunities.length === 0 && !loading ? (
        <div className="flex h-32 w-full items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      ) : (
        <ScrollableTableWrapper height="600px">
          <Table
            striped
            hoverable
            className="w-full table-auto drop-shadow-none"
          >
            <Table.Head>
              {columnKeys.map((columnKey, colIndex) => (
                <Table.HeadCell
                  key={columnKey}
                  className={`whitespace-nowrap px-6 py-3 text-left ${
                    colIndex === 0
                      ? "sticky left-0 z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-1">
                    {t(`pages.forecast.table.columns.${columnKey}`)}

                    {COLUMN_TOOLTIPS[columnKey] && (
                      <Tooltip
                        content={
                          <TooltipContent
                            title={COLUMN_TOOLTIPS[columnKey].title}
                            size="lg"
                            text={COLUMN_TOOLTIPS[columnKey].text()}
                          />
                        }
                        // eslint-disable-next-line react/style-prop-object
                        style="light"
                      >
                        <HiOutlineInformationCircle className="opacity-50" />
                      </Tooltip>
                    )}
                  </div>
                </Table.HeadCell>
              ))}
            </Table.Head>
            <Table.Body className="divide-y">
              {paginatedOpportunities.map((opp, rowIndex) => (
                <Table.Row key={opp.accountId || opp.id || rowIndex}>
                  {columnKeys.map((columnKey, colIndex) => {
                    let cellContent = opp[columnKey];
                    const isFirstColumn = colIndex === 0;
                    const rowBgColor =
                      rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50";

                    // Special handling for different column types
                    if (
                      columnKey === ALL_COLUMN_KEYS.ACCOUNT_NAME &&
                      cellContent
                    ) {
                      const accountName = cellContent?.name || cellContent;
                      return (
                        <Table.Cell
                          key={columnKey}
                          className={`max-w-xs px-6 py-4 ${
                            isFirstColumn
                              ? `sticky left-0 z-10 ${rowBgColor} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`
                              : ""
                          }`}
                        >
                          <Link
                            className="space-y-1 font-bold hover:text-blue-500 hover:underline"
                            to={`/customers/${opp.accountId}`}
                          >
                            <Avatar
                              size="xs"
                              img={opp?.logo}
                              className="flex justify-start font-bold"
                            >
                              <span className="truncate" title={accountName}>
                                {accountName}
                              </span>
                            </Avatar>
                          </Link>
                        </Table.Cell>
                      );
                    }

                    // deltaMrr and deltaArr are already formatted strings from the API

                    return (
                      <Table.Cell
                        key={columnKey}
                        className={`max-w-[340px] truncate px-6 py-4 ${
                          isFirstColumn
                            ? `sticky left-0 z-10 ${rowBgColor} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`
                            : ""
                        }`}
                        title={cellContent || "—"}
                      >
                        {cellContent || "—"}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}

              {/* Total row */}
              <Table.Row className="bg-gray-50 font-bold text-black">
                {columnKeys.map((columnKey, colIndex) => {
                  // deltaMrr and deltaArr are already formatted strings from the API
                  const cellContent = totalRow[columnKey];
                  const isFirstColumn = colIndex === 0;

                  return (
                    <Table.Cell
                      key={columnKey}
                      className={`whitespace-nowrap px-6 py-4 ${
                        isFirstColumn
                          ? "sticky left-0 z-10 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                          : ""
                      }`}
                    >
                      {cellContent || "—"}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            </Table.Body>
          </Table>
        </ScrollableTableWrapper>
      )}

      {!isRenewalForecast && opportunities.length > 0 && (
        <div className="flex w-full items-center justify-between border-t bg-white px-5 py-4 dark:bg-gray-900">
          <PaginationInfo
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={opportunities.length}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              showIcons
              previousLabel=""
              nextLabel=""
            />
          )}
        </div>
      )}
    </div>
  );
};

export const ForecastPageTable = ({
  selectedFilter,
  selectedTimeRange = "Closed this quarter",
  showOnlyOpen = false,
  showOnlyClosed = false,
}) => {
  // Change this from useRenewalForecast() to useForecastContext()
  const {
    transformDataForRenewalForecast,
    transformDataForDetailedView,
    getTotalsForDetailedView,
    getGrandTotalsFromPerType,
    loading,
    getTimeRangeData,
    fetchMultipleTimeRanges,
  } = useForecastContext();

  const [openOpportunities, setOpenOpportunities] = useState([]);
  const [closedOpportunities, setClosedOpportunities] = useState([]);
  const [openTotals, setOpenTotals] = useState(null);
  const [closedTotals, setClosedTotals] = useState(null);
  const [openGrandTotals, setOpenGrandTotals] = useState(null);
  const [closedGrandTotals, setClosedGrandTotals] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const isRenewalForecast =
    selectedFilter === LEGACY_FILTER_KEYS.RENEWAL_FORECAST;

  useEffect(() => {
    const loadData = async () => {
      setDataLoaded(false);

      try {
        // For closed deals, we need to determine the source of the data
        let dataSourceTimeRange = selectedTimeRange;

        // If we're showing closed opportunities and the selected time range is a "closed" range,
        // we need to map it to the appropriate forecast time range that contains the data
        if (
          selectedFilter === LEGACY_FILTER_KEYS.RENEWED_ACCOUNTS ||
          (selectedTimeRange &&
            selectedTimeRange.toLowerCase().includes("closed"))
        ) {
          // For now, closed deals data only comes from "Forecast for this quarter"
          // In the future, this mapping could be more sophisticated
          if (selectedTimeRange === "Closed this quarter") {
            dataSourceTimeRange = "Forecast for this quarter";
          } else if (selectedTimeRange === "Closed last quarter") {
            dataSourceTimeRange = "Forecast for this quarter"; // Fallback for now
          } else if (selectedTimeRange === "Closed last 2 quarters") {
            dataSourceTimeRange = "Forecast for this quarter"; // Fallback for now
          }
        }

        // Check if we already have cached data for this time range
        const cachedData = getTimeRangeData(dataSourceTimeRange);

        let data = cachedData;

        if (!cachedData) {
          // Fetch new data if not cached
          await fetchMultipleTimeRanges([dataSourceTimeRange]);
          // Get the newly cached data
          data = getTimeRangeData(dataSourceTimeRange);
        } else {
          console.log("Using cached data");
        }

        if (data) {
          if (isRenewalForecast) {
            // For renewal forecast, use per-type aggregated data
            const transformed = transformDataForRenewalForecast(data);

            const { openOpportunities: open, closedOpportunities: closed } =
              transformed;
            setOpenOpportunities(open || []);
            setClosedOpportunities(closed || []);

            // Get grand totals for total rows
            const openGrandTotals = getGrandTotalsFromPerType(data, true);
            const closedGrandTotals = getGrandTotalsFromPerType(data, false);

            setOpenGrandTotals(openGrandTotals);
            setClosedGrandTotals(closedGrandTotals);
          } else {
            // For other views, use per-account detailed data
            const openDeals = transformDataForDetailedView(data, true);
            const closedDeals = transformDataForDetailedView(data, false);

            setOpenOpportunities(openDeals || []);
            setClosedOpportunities(closedDeals || []);

            // Get totals for detailed views
            const openTotals = getTotalsForDetailedView(data, true);
            const closedTotals = getTotalsForDetailedView(data, false);

            setOpenTotals(openTotals);
            setClosedTotals(closedTotals);
          }
        } else {
          // Reset state if no data
          setOpenOpportunities([]);
          setClosedOpportunities([]);
          setOpenTotals(null);
          setClosedTotals(null);
          setOpenGrandTotals(null);
          setClosedGrandTotals(null);
        }
      } catch (error) {
        console.error("Error loading forecast data:", error);
        // Reset state on error
        setOpenOpportunities([]);
        setClosedOpportunities([]);
        setOpenTotals(null);
        setClosedTotals(null);
        setOpenGrandTotals(null);
        setClosedGrandTotals(null);
      } finally {
        setDataLoaded(true);
      }
    };

    // Only load data if we have a valid time range
    if (selectedTimeRange) {
      loadData();
    } else {
      console.warn("No selectedTimeRange provided, skipping data load");
    }
  }, [
    selectedTimeRange,
    selectedFilter,
    fetchMultipleTimeRanges,
    transformDataForRenewalForecast,
    transformDataForDetailedView,
    getTotalsForDetailedView,
    getGrandTotalsFromPerType,
    getTimeRangeData,
    isRenewalForecast,
  ]);

  // Show loading state until data is loaded
  const isLoading = loading || !dataLoaded;

  // Handle tab-specific rendering
  if (showOnlyOpen) {
    return (
      <ForecastPageTableTemplate
        title="Open Deals"
        totalRowTitle="Total (all open opps)"
        opportunities={openOpportunities}
        selectedFilter={selectedFilter}
        loading={isLoading}
        grandTotals={openGrandTotals}
        totals={openTotals}
        titleTooltip={{
          title: "About Open Deals",
          text: (
            <div>
              This table lists all deals that are forecasted to close within the
              selected period (e.g., 'Due this quarter') but are not yet
              finalized. This is your active pipeline of renewals and
              expansions.
            </div>
          ),
        }}
      />
    );
  }

  if (showOnlyClosed) {
    return (
      <ForecastPageTableTemplate
        title="Closed Opportunities"
        totalRowTitle="Total"
        opportunities={closedOpportunities}
        selectedFilter={selectedFilter}
        loading={isLoading}
        grandTotals={closedGrandTotals}
        totals={closedTotals}
        titleTooltip={{
          title: "About Closed Deals",
          text: (
            <div>
              This table lists all opportunities (renewals, expansions, etc.)
              that have been finalized and closed within the selected period
              (e.g., 'Closed this quarter'). This view shows your actual,
              realized revenue changes.
            </div>
          ),
        }}
      />
    );
  }

  // Default behavior for renewal forecast (show both tables)
  if (isRenewalForecast) {
    return (
      <>
        <ForecastPageTableTemplate
          title="Open Deals"
          totalRowTitle="Total"
          opportunities={openOpportunities}
          selectedFilter={selectedFilter}
          loading={isLoading}
          grandTotals={openGrandTotals}
        />
        <ForecastPageTableTemplate
          title="Closed Deals"
          totalRowTitle="Total"
          opportunities={closedOpportunities}
          selectedFilter={selectedFilter}
          loading={isLoading}
          grandTotals={closedGrandTotals}
        />
      </>
    );
  }

  // Fallback to showing open opportunities
  return (
    <ForecastPageTableTemplate
      title="Opportunities"
      totalRowTitle="Total"
      opportunities={openOpportunities}
      selectedFilter={selectedFilter}
      loading={isLoading}
      totals={openTotals}
    />
  );
};
